// * This is the main sidebar
import { useEffect, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { AGENT_ENABLED } from '../../featureFlags';
import { Box, Divider, Typography } from '@mui/material';
import { ReactComponent as VestmapLogo } from './../../img/vestmaplogo.svg';

// Run Report Icon
import { ReactComponent as HomeIconLight } from '@/../../public/homeiconlight.svg';
import { ReactComponent as HomeIconDark } from '@/../../public/homeicondark.svg';

// Bulk Icon
import { ReactComponent as BulkIconDark } from '@/../../public/bulkicondark.svg';
import { ReactComponent as BulkIconLight } from '@/../../public/bulkiconlight.svg';

// Learn Icon
import { ReactComponent as LearnIcon } from '@/../../public/learnicondark.svg';

// Explore Icon
import { ReactComponent as ExploreIconDark } from '@/../../public/msaIconDark.svg';
import { ReactComponent as ExploreIconLight } from '@/../../public/msaIconLight.svg';

// DropDown Icon
import { ReactComponent as DropDown } from '@/../../public/dropdowndark.svg';

import { ReactComponent as AdminIconDark } from '@/../../public/adminIconDark.svg';
import { ReactComponent as AdminIconLight } from '@/../../public/adminIconLight.svg';

// Report Icon
import { ReactComponent as ReportDark } from '@/../../public/ReportDark.svg';
import { ReactComponent as ReportLight } from '@/../../public/ReportLight.svg';

import { useAuth } from '../../AuthProvider';
import { MessageSquare, Compass, Building2, Bot } from 'lucide-react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const styles = {
  selected: {
    backgroundColor: '#39585C',
    color: '#FFFFFF',
  },

  unselected: {
    color: '#39585C',
    fontWeight: 400,
    fontStyle: 'normal',
  },

  itemBoxStyle: {
    display: 'flex',
    height: '1.1rem',
    padding: '1rem',
    alignItems: 'center',
    gap: '0.75rem',
    alignSelf: 'stretch',
    borderRadius: '0.75rem',
  },

  itemTextStyle: {
    fontFamily: '"Lato", sans-serif',

    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '150%',
    letterSpacing: '0.0125rem',
  },
};

const RunReportsSidebar = ({ mobileView }) => {
  const [admin, setAdmin] = useState(false);
  const { user, getAccessToken } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();

  async function checkIfAdmin() {
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/admin/check-admin`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },

        body: JSON.stringify({ email: user.Email }),
      });

      const data = await response.json();

      if (data.isAdmin === true) setAdmin(true);
      else {
        const admin_emails = ['clay@vestmap.com', 'clay@authenticaudience.co'];
        if (admin_emails.includes(user?.email?.toLowerCase())) {
          setAdmin(true);
        }
      }
    } catch (error) {
      console.log({ error });
    }
  }

  useEffect(() => {
    if (user) {
      checkIfAdmin();
    }
  }, [user]);
  return (
    <Box
      sx={{
        backgroundColor: '#F9F9F9',
        flexShrink: 0,
        display: {
          xs: 'none',
          sm: 'flex',
        },
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1.25rem',
        gap: '2.2rem',
        position: 'relative',

        boxShadow: {
          xs: 'none',
          sm: '0px 4px 4px 0px #00000040',
        },
      }}
    >
      <Box sx={{ display: 'flex', paddingTop: '0.75rem' }}>
        <Link to='/run-reports'>
          <VestmapLogo
            style={{
              width: '15.25rem',
              maxWidth: '100%',
              height: '1.95438rem',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          />
        </Link>
      </Box>

      <Divider
        sx={{
          width: '13.3rem',
          maxWidth: '100%',
          // width: {
          //   xs: '100%',
          //   md: '13.3rem',
          // },
          height: '0.0625rem',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '1rem',
          width: '13.625rem',
          // width: {
          //   xs: '100%',
          //   md: '13.3rem',
          // },
          maxWidth: '100%',

          '@media (max-width: 600px)': {
            transform: 'translateX(-200%)',
            width: ' 100%',
            boxShadow: ' 0 0 10px 5px #efefef',
            textAlign: ' center',
            transition: ' all 0.5s linear',
          },
        }}
      >
        {AGENT_ENABLED && (
        <Link to='/agent' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,
              location.pathname === '/agent'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            {location.pathname === '/agent' ? (
              <MessageSquare
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            ) : (
              <MessageSquare
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            )}

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/agent'
                      ? styles.selected.color
                      : styles.unselected.color,
                  fontWeight: location.pathname === '/agent' ? 800 : 400,
                },
              ]}
            >
              Agent
            </Typography>
          </Box>
        </Link>
        )}

        <Link to='/discover' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,
              location.pathname === '/discover'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            <Compass
              style={{
                width: '1.25rem',
                height: '1.25rem',
                strokeWidth: location.pathname === '/discover' ? 2.5 : 2,
              }}
            />

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/discover'
                      ? styles.selected.color
                      : styles.unselected.color,
                  fontWeight: location.pathname === '/discover' ? 800 : 400,
                },
              ]}
            >
              Discover
            </Typography>
          </Box>
        </Link>

        <Link to='/rent-advisor' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,
              location.pathname === '/rent-advisor'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            <Building2
              style={{
                width: '1.25rem',
                height: '1.25rem',
                strokeWidth: location.pathname === '/rent-advisor' ? 2.5 : 2,
              }}
            />

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/rent-advisor'
                      ? styles.selected.color
                      : styles.unselected.color,
                  fontWeight: location.pathname === '/rent-advisor' ? 800 : 400,
                },
              ]}
            >
              Rent Advisor
            </Typography>
          </Box>
        </Link>

        <Link to='/run-reports' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,
              location.pathname === '/run-reports'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            {location.pathname === '/run-reports' ? (
              <HomeIconLight
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            ) : (
              <HomeIconDark
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            )}

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/run-reports' ||
                    location.pathname === '/'
                      ? styles.selected.color
                      : styles.unselected.color,
                  fontWeight:
                    location.pathname === '/run-reports' ||
                    location.pathname === '/'
                      ? 800
                      : 400,
                },
              ]}
            >
              My Reports
            </Typography>
          </Box>
        </Link>

        <Link to='/mcp' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,
              location.pathname === '/mcp'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            <Bot
              style={{
                width: '1.25rem',
                height: '1.25rem',
                strokeWidth: location.pathname === '/mcp' ? 2.5 : 2,
              }}
            />

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/mcp'
                      ? styles.selected.color
                      : styles.unselected.color,
                  fontWeight:
                    location.pathname === '/mcp' ? 800 : 400,
                },
              ]}
            >
              Agent MCP
            </Typography>
          </Box>
        </Link>

        {admin && (
          <Link to='/admin-panel' style={{ width: '100%' }}>
            <Box
              sx={[
                styles.itemBoxStyle,
                location.pathname === '/admin-panel'
                  ? styles.selected
                  : styles.unselected,
              ]}
            >
              {location.pathname === '/admin-panel' ? (
                <AdminIconLight
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                  }}
                />
              ) : (
                <AdminIconDark
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                  }}
                />
              )}

              <Typography
                sx={[
                  styles.itemTextStyle,
                  {
                    color:
                      location.pathname === '/admin-panel'
                        ? styles.selected.color
                        : styles.unselected.color,
                    fontWeight:
                      location.pathname === '/admin-panel' ? 800 : 400,
                  },
                ]}
              >
                Admin Panel
              </Typography>
            </Box>
          </Link>
        )}

        {/* <Link to='/bulk-import' style={{ width: '100%' }}>
          <Box
            sx={[
              styles.itemBoxStyle,

              location.pathname === '/bulk-import'
                ? styles.selected
                : styles.unselected,
            ]}
          >
            {location.pathname === '/bulk-import' ? (
              <BulkIconLight
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            ) : (
              <BulkIconDark
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                }}
              />
            )}

            <Typography
              sx={[
                styles.itemTextStyle,
                {
                  color:
                    location.pathname === '/bulk-import'
                      ? styles.selected.color
                      : styles.unselected.color,

                  fontWeight: location.pathname === '/bulk-import' ? 800 : 400,
                },
              ]}
            >
              Bulk Imports
            </Typography>
          </Box>
        </Link> */}

        {/* <NestedAccordion currentPage={location.pathname} /> */}
        {admin && (
          <Link to='/templates' style={{ width: '100%' }}>
            <Box
              sx={[
                styles.itemBoxStyle,
                location.pathname === '/templates'
                  ? styles.selected
                  : styles.unselected,
              ]}
            >
              {location.pathname === '/templates' ? (
                <ReportLight
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                  }}
                />
              ) : (
                <ReportDark
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                  }}
                />
              )}

              <Typography
                sx={[
                  styles.itemTextStyle,
                  {
                    color:
                      location.pathname === '/templates'
                        ? styles.selected.color
                        : styles.unselected.color,
                    fontWeight: location.pathname === '/templates' ? 800 : 400,
                  },
                ]}
              >
                Report Templates
              </Typography>
            </Box>
          </Link>
        )}

        {AGENT_ENABLED && admin && (
          <Link to='/agent_v2' style={{ width: '100%' }}>
            <Box
              sx={[
                styles.itemBoxStyle,
                location.pathname === '/agent_v2'
                  ? styles.selected
                  : styles.unselected,
              ]}
            >
              <Box
                sx={{
                  width: '1.25rem',
                  height: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                }}
              >
                {location.pathname === '/agent_v2' ? '⚡' : '⚡'}
              </Box>

              <Typography
                sx={[
                  styles.itemTextStyle,
                  {
                    color:
                      location.pathname === '/agent_v2'
                        ? styles.selected.color
                        : styles.unselected.color,
                    fontWeight: location.pathname === '/agent_v2' ? 800 : 400,
                  },
                ]}
              >
                Agent V2
              </Typography>
            </Box>
          </Link>
        )}

        <Box
          sx={[
            styles.itemBoxStyle,
            styles.unselected,
            {
              borderRadius: '0',
              cursor: 'pointer',
              borderTop: '1px solid #E2E8F0',
              position: 'absolute', // Add this
              bottom: 0, // Add this
              left: 0, // Add this
              width: '80%',
              marginLeft: '1rem',
            },
          ]}
          onClick={() => {
            window.open(
              'https://vestmap.notion.site/VestMap-Data-Dictionary-01d8ebd075af4bea9c7ad0cb6dff6698',
              '_blank',
            );
          }}
        >
          <LearnIcon
            style={{
              width: '1.25rem',
              height: '1.25rem',
            }}
          />

          <Typography
            sx={[
              styles.itemTextStyle,
              {
                color: styles.unselected.color,
              },
            ]}
          >
            Learn to Use VestMap
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const NestedAccordion = ({ currentPage }) => (
  <div style={{ width: '100%' }}>
    <Accordion
      disableGutters
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            style={{
              color: currentPage === '/zipfinder' ? '#FFFFFF' : '#39585C',
            }}
          />
        }
        aria-controls='panel1a-content'
        id='panel1a-header'
        sx={[
          styles.itemBoxStyle,
          currentPage === '/zipfinder' ? styles.selected : styles.unselected,
        ]}
      >
        {currentPage === '/zipfinder' ? (
          <ExploreIconLight
            style={{
              width: '1.25rem',
              height: '1.25rem',
            }}
          />
        ) : (
          <ExploreIconDark
            style={{
              width: '1.25rem',
              height: '1.25rem',
            }}
          />
        )}
        <Typography
          sx={[
            styles.itemTextStyle,
            {
              color:
                currentPage === '/zipfinder'
                  ? styles.selected.color
                  : styles.unselected.color,
              marginLeft: '8%',
              fontWeight: currentPage === '/zipfinder' ? 800 : 400,
            },
          ]}
        >
          Explore Markets
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <List sx={{ borderLeft: '2px solid grey' }}>
          <Link to='/zipfinder'>
            <ListItem>
              <ListItemText
                disableTypography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '0.875rem',
                  fontStyle: 'normal',
                  fontWeight: currentPage === '/zipfinder' ? 800 : 500,
                  lineHeight: '150%',
                  letterSpacing: '0.0125rem',
                  color: currentPage === '/zipfinder' ? '#39585C' : '#000000',
                  cursor: 'pointer',
                }}
                primary='ZipFinder'
              />
            </ListItem>
          </Link>
          {/* <ListItem>
            <ListItemText
              disableTypography
              sx={{
                fontFamily: 'Lato',
                fontSize: '0.875rem',
                fontStyle: 'normal',
                lineHeight: '150%',
                letterSpacing: '0.0125rem',
                fontWeight: 700,
              }}
              primary='Other Source'
            />
          </ListItem> */}
        </List>
      </AccordionDetails>
    </Accordion>
  </div>
);

export default RunReportsSidebar;
